import { SETTING_KEYS } from "@/constants/settings";
import {
  describeSettingDuration,
  getSettingDateRange,
  getSettingDeadline,
  isWithinSettingDeadline,
} from "@/hooks/useSettings";

export type SaleChangeRequestType = "cancel" | "edit";

export interface SaleChangeRequestWindow {
  /** Se a solicitação ainda pode ser feita. */
  allowed: boolean;
  /** Instante limite calculado, quando existe prazo configurado. */
  deadline: Date | null;
  /** Prazo configurado em formato legível (ex.: "24 horas"). */
  limitLabel: string | null;
  /** Mensagem pronta para exibição quando o prazo já expirou. */
  message: string | null;
}

const SETTING_KEY_BY_TYPE: Record<SaleChangeRequestType, string> = {
  cancel: SETTING_KEYS.SALE_CANCEL_REQUEST_TIME_LIMIT,
  edit: SETTING_KEYS.SALE_EDIT_REQUEST_TIME_LIMIT,
};

const PERIOD_KEY_BY_TYPE: Record<SaleChangeRequestType, string> = {
  cancel: SETTING_KEYS.SALE_CANCEL_REQUEST_PERIOD,
  edit: SETTING_KEYS.SALE_EDIT_REQUEST_PERIOD,
};

const formatDate = (date: Date) => date.toLocaleDateString("pt-BR");

/**
 * Avalia o período (intervalo de datas) em que as solicitações são aceitas.
 * Vale a data de hoje, não a da venda. Sem período configurado, libera.
 */
const evaluatePeriod = (
  type: SaleChangeRequestType,
  actionLabel: string
): string | null => {
  const range = getSettingDateRange(PERIOD_KEY_BY_TYPE[type]);

  if (!range) return null;

  const now = Date.now();

  if (range.start && now < range.start.getTime()) {
    return `O período para solicitar ${actionLabel} de venda ainda não começou. As solicitações são aceitas a partir de ${formatDate(range.start)}.`;
  }

  if (range.end && now > range.end.getTime()) {
    return `O período para solicitar ${actionLabel} de venda está encerrado. As solicitações eram aceitas até ${formatDate(range.end)}.`;
  }

  return null;
};

const ACTION_LABEL_BY_TYPE: Record<SaleChangeRequestType, string> = {
  cancel: "cancelamento",
  edit: "edição",
};

/**
 * Avalia o prazo de solicitação de alteração/cancelamento de uma venda.
 *
 * Duas regras independentes, ambas definidas pelo admin em Configurações do
 * Sistema, e ambas precisam passar:
 *   1. período — intervalo de datas em que solicitações são aceitas (vale hoje);
 *   2. prazo — janela em horas contada a partir da criação da venda.
 *
 * Sem configuração, ou para usuários isentos (admins), a solicitação segue
 * liberada — mesma regra aplicada no backend.
 */
export const getSaleChangeRequestWindow = (
  type: SaleChangeRequestType,
  createdAt?: string | Date | null,
  isExempt = false
): SaleChangeRequestWindow => {
  const key = SETTING_KEY_BY_TYPE[type];
  const limitLabel = describeSettingDuration(key);
  const actionLabel = ACTION_LABEL_BY_TYPE[type];

  if (isExempt) {
    return { allowed: true, deadline: null, limitLabel, message: null };
  }

  // Regra 1: período (intervalo de datas) em que solicitações são aceitas
  const periodMessage = evaluatePeriod(type, actionLabel);

  if (periodMessage) {
    return {
      allowed: false,
      deadline: createdAt ? getSettingDeadline(key, createdAt) : null,
      limitLabel,
      message: periodMessage,
    };
  }

  if (!createdAt) {
    return { allowed: true, deadline: null, limitLabel, message: null };
  }

  // Regra 2: prazo em horas contado da criação da venda
  const deadline = getSettingDeadline(key, createdAt);

  if (isWithinSettingDeadline(key, createdAt)) {
    return { allowed: true, deadline, limitLabel, message: null };
  }

  const message = limitLabel
    ? `O prazo para solicitar ${actionLabel} desta venda expirou. A solicitação só pode ser feita em até ${limitLabel} após a criação da venda.`
    : `O prazo para solicitar ${actionLabel} desta venda expirou.`;

  return { allowed: false, deadline, limitLabel, message };
};
